/** ONE CONTINUITY — dialogs. No secrets: only credential LOCATION text allowed. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Modal, Field } from "../operations/StockInventoryDialogs";

const inputCls = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";
const SCOPES = ["BUSINESS", "PERSONAL"];

const scopeOptions = (access) =>
  SCOPES.filter((s) => (s === "BUSINESS" && access.business) || (s === "PERSONAL" && access.personal));

function ScopeSelect({ value, onChange, access, testid }) {
  return (
    <Field label="Scope" required>
      <select value={value} onChange={onChange} data-testid={testid} className={inputCls}>
        {scopeOptions(access).map((s) => <option key={s} value={s}>{s}</option>)}
      </select></Field>);
}

async function save(path, item, payload, onDone) {
  try {
    if (item.id) await api.patch(`${path}/${item.id}`, payload);
    else await api.post(path, payload);
    toast.success("Saved"); onDone();
  } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
}

// ------------------------------------------------------------------ PERSON
export function PersonDialog({ person, access, onClose, onDone }) {
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ scope: person.scope || scopeOptions(access)[0],
    category: person.category || "CA_ACCOUNTANT", name: person.name || "",
    organisation: person.organisation || "", role: person.role || "",
    phone: person.phone || "", email: person.email || "",
    why_important: person.why_important || "", handles: person.handles || "",
    related_refs: person.related_refs || "", notes: person.notes || "" });
  useEffect(() => {
    api.get("/continuity/config").then(({ data }) => setCats(data.config.adviser_categories || []));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={person.id ? "Edit key person" : "Add key person"} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="cont-person-dialog">
        <ScopeSelect value={f.scope} onChange={set("scope")} access={access} testid="cont-pd-scope" />
        <Field label="Category" required>
          <select value={f.category} onChange={set("category")} data-testid="cont-pd-category" className={inputCls}>
            {cats.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Name" required>
          <input value={f.name} onChange={set("name")} data-testid="cont-pd-name" className={inputCls} /></Field>
        <Field label="Organisation">
          <input value={f.organisation} onChange={set("organisation")} className={inputCls} /></Field>
        <Field label="Role">
          <input value={f.role} onChange={set("role")} className={inputCls} /></Field>
        <Field label="Phone">
          <input value={f.phone} onChange={set("phone")} data-testid="cont-pd-phone" className={inputCls} /></Field>
        <Field label="Email">
          <input value={f.email} onChange={set("email")} className={inputCls} /></Field>
        <div className="col-span-2">
          <Field label="Why important">
            <input value={f.why_important} onChange={set("why_important")} data-testid="cont-pd-why" className={inputCls} /></Field>
        </div>
        <div className="col-span-3">
          <Field label="What they handle">
            <input value={f.handles} onChange={set("handles")} className={inputCls} /></Field>
        </div>
        <div className="col-span-3">
          <Field label="Notes / related documents / related customer-vendor">
            <input value={f.notes} onChange={set("notes")} className={inputCls} /></Field>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={() => f.name ? save("/continuity/people", person, f, onDone) : toast.error("Name required")}
            data-testid="cont-pd-save" className="text-[11px] px-3 py-1.5 rounded-md bg-emerald-950 text-white">Save</button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------- RESPONSIBILITY
export function ResponsibilityDialog({ item, onClose, onDone }) {
  const [fns, setFns] = useState([]);
  const [f, setF] = useState({ scope: "BUSINESS", function: item.function || "",
    primary: item.primary || "", backup: item.backup || "",
    escalation: item.escalation || "", related_module: item.related_module || "",
    instructions: item.instructions || "" });
  useEffect(() => {
    api.get("/continuity/config").then(({ data }) => setFns(data.config.functions || []));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={item.id ? "Edit responsibility" : "Add responsibility"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="cont-resp-dialog">
        <Field label="Function" required>
          <input value={f.function} onChange={set("function")} list="cont-fns"
              data-testid="cont-rd-function" className={inputCls} />
          <datalist id="cont-fns">{fns.map((x) => <option key={x} value={x} />)}</datalist></Field>
        <Field label="Primary responsible" required>
          <input value={f.primary} onChange={set("primary")} data-testid="cont-rd-primary" className={inputCls} /></Field>
        <Field label="Backup">
          <input value={f.backup} onChange={set("backup")} data-testid="cont-rd-backup" className={inputCls} /></Field>
        <Field label="Escalation">
          <input value={f.escalation} onChange={set("escalation")} className={inputCls} /></Field>
        <Field label="Related module path (e.g. /commercial/crm/quotations)">
          <input value={f.related_module} onChange={set("related_module")} className={inputCls} /></Field>
        <Field label="Instructions">
          <input value={f.instructions} onChange={set("instructions")} className={inputCls} /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={() => f.function && f.primary
            ? save("/continuity/responsibilities", item, f, onDone)
            : toast.error("Function and primary required")}
            data-testid="cont-rd-save" className="text-[11px] px-3 py-1.5 rounded-md bg-emerald-950 text-white">Save</button>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------- INSTRUCTION
export function InstructionDialog({ item, access, onClose, onDone }) {
  const PH = ["IMMEDIATE", "FIRST_24_HOURS", "FIRST_7_DAYS", "FIRST_30_DAYS", "ONGOING"];
  const [f, setF] = useState({ scope: item.scope || scopeOptions(access)[0],
    phase: item.phase || "IMMEDIATE", action: item.action || "",
    responsible: item.responsible || "", backup: item.backup || "",
    related_module: item.related_module || "", related_contact: item.related_contact || "",
    priority: item.priority || "", notes: item.notes || "",
    order: item.order || 0 });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={item.id ? "Edit instruction" : "Add playbook instruction"} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="cont-play-dialog">
        <ScopeSelect value={f.scope} onChange={set("scope")} access={access} testid="cont-id-scope" />
        <Field label="Phase" required>
          <select value={f.phase} onChange={set("phase")} data-testid="cont-id-phase" className={inputCls}>
            {PH.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Priority">
          <select value={f.priority} onChange={set("priority")} className={inputCls}>
            {["", "CRITICAL", "HIGH", "NORMAL"].map((p) => <option key={p} value={p}>{p || "—"}</option>)}
          </select></Field>
        <div className="col-span-3">
          <Field label="Action" required>
            <input value={f.action} onChange={set("action")} data-testid="cont-id-action" className={inputCls}
                placeholder="e.g. Check pending Customer Orders" /></Field>
        </div>
        <Field label="Responsible person">
          <input value={f.responsible} onChange={set("responsible")} data-testid="cont-id-responsible" className={inputCls} /></Field>
        <Field label="Backup person">
          <input value={f.backup} onChange={set("backup")} className={inputCls} /></Field>
        <Field label="Related contact">
          <input value={f.related_contact} onChange={set("related_contact")} className={inputCls} /></Field>
        <Field label="Related module path">
          <input value={f.related_module} onChange={set("related_module")} className={inputCls}
              placeholder="/commercial/crm/customer-pos" /></Field>
        <Field label="Order">
          <input type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })}
              className={inputCls} /></Field>
        <Field label="Notes / due">
          <input value={f.notes} onChange={set("notes")} className={inputCls} /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={() => f.action ? save("/continuity/playbook", item, f, onDone) : toast.error("Action required")}
            data-testid="cont-id-save" className="text-[11px] px-3 py-1.5 rounded-md bg-emerald-950 text-white">Save</button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- INSURANCE
export function InsuranceDialog({ item, access, onClose, onDone }) {
  const [f, setF] = useState({ scope: item.scope || scopeOptions(access)[0],
    policy_holder: item.policy_holder || "", insurer: item.insurer || "",
    policy_number: item.policy_number || "", policy_type: item.policy_type || "CORPORATE",
    coverage_summary: item.coverage_summary || "", start_date: item.start_date || "",
    end_date: item.end_date || "", premium: item.premium ?? "",
    nominee: item.nominee || "", nomination_status: item.nomination_status || "NOT_APPLICABLE",
    nominee_verified_date: item.nominee_verified_date || "", adviser: item.adviser || "",
    credential_location: item.credential_location || "", status: item.status || "ACTIVE",
    notes: item.notes || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = () => {
    if (!f.policy_holder || !f.insurer || !f.policy_number) {
      toast.error("Holder, insurer and policy number required"); return;
    }
    save("/continuity/insurance", item,
      { ...f, premium: f.premium === "" ? null : Number(f.premium) }, onDone);
  };
  return (
    <Modal title={item.id ? "Edit policy" : "Add insurance policy"} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="cont-ins-dialog">
        <ScopeSelect value={f.scope} onChange={set("scope")} access={access} testid="cont-insd-scope" />
        <Field label="Policy type" required>
          <select value={f.policy_type} onChange={set("policy_type")} data-testid="cont-insd-type" className={inputCls}>
            {["CORPORATE", "PERSONAL", "PROPERTY", "VEHICLE", "OTHER"].map((t) => <option key={t}>{t}</option>)}
          </select></Field>
        <Field label="Status">
          <select value={f.status} onChange={set("status")} className={inputCls}>
            {["ACTIVE", "LAPSED", "CLOSED"].map((s) => <option key={s}>{s}</option>)}
          </select></Field>
        <Field label="Policy holder" required>
          <input value={f.policy_holder} onChange={set("policy_holder")} data-testid="cont-insd-holder" className={inputCls} /></Field>
        <Field label="Insurer" required>
          <input value={f.insurer} onChange={set("insurer")} data-testid="cont-insd-insurer" className={inputCls} /></Field>
        <Field label="Policy number" required>
          <input value={f.policy_number} onChange={set("policy_number")} data-testid="cont-insd-number" className={inputCls} /></Field>
        <Field label="Start date">
          <input type="date" value={f.start_date} onChange={set("start_date")} className={inputCls} /></Field>
        <Field label="End date">
          <input type="date" value={f.end_date} onChange={set("end_date")} data-testid="cont-insd-end" className={inputCls} /></Field>
        <Field label="Premium">
          <input type="number" value={f.premium} onChange={set("premium")} className={inputCls} /></Field>
        <Field label="Nominee">
          <input value={f.nominee} onChange={set("nominee")} data-testid="cont-insd-nominee" className={inputCls} /></Field>
        <Field label="Nomination status">
          <select value={f.nomination_status} onChange={set("nomination_status")}
              data-testid="cont-insd-nomstatus" className={inputCls}>
            {["CONFIRMED", "NOT_CONFIRMED", "REVIEW_REQUIRED", "NOT_APPLICABLE"].map((s) =>
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Nominee verified date">
          <input type="date" value={f.nominee_verified_date} onChange={set("nominee_verified_date")} className={inputCls} /></Field>
        <Field label="Adviser">
          <input value={f.adviser} onChange={set("adviser")} className={inputCls} /></Field>
        <div className="col-span-2">
          <Field label="Coverage summary">
            <input value={f.coverage_summary} onChange={set("coverage_summary")} className={inputCls} /></Field>
        </div>
        <div className="col-span-3">
          <Field label="Credential location / access instruction (NEVER the secret itself)">
            <input value={f.credential_location} onChange={set("credential_location")}
                data-testid="cont-insd-credloc" className={inputCls}
                placeholder="e.g. Stored in authorised family password manager" /></Field>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={submit} data-testid="cont-insd-save"
            className="text-[11px] px-3 py-1.5 rounded-md bg-emerald-950 text-white">Save policy</button>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------------- GRANT
export function GrantDialog({ onClose, onDone }) {
  const [f, setF] = useState({ email: "", profile: "BUSINESS_SUCCESSOR", scopes: ["BUSINESS"] });
  const toggle = (s) => setF({ ...f, scopes: f.scopes.includes(s)
    ? f.scopes.filter((x) => x !== s) : [...f.scopes, s] });
  const submit = async () => {
    if (!f.email || !f.scopes.length) { toast.error("Email and at least one scope required"); return; }
    try {
      await api.post("/continuity/grants", f);
      toast.success("Access granted"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="Grant continuity access" onClose={onClose}>
      <div className="space-y-3" data-testid="cont-grant-dialog">
        <Field label="Email" required>
          <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
              data-testid="cont-gd-email" className={inputCls} /></Field>
        <Field label="Profile" required>
          <select value={f.profile} onChange={(e) => setF({ ...f, profile: e.target.value })}
              data-testid="cont-gd-profile" className={inputCls}>
            {["OWNER", "BUSINESS_SUCCESSOR", "FAMILY_AUTHORISED", "FINANCE", "LEGAL_ADVISER", "SUPER_ADMIN"]
              .map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
          </select></Field>
        <div className="flex gap-4 text-[11.5px]">
          {SCOPES.map((s) => (
            <label key={s} className="inline-flex items-center gap-1.5">
              <input type="checkbox" checked={f.scopes.includes(s)} onChange={() => toggle(s)}
                  data-testid={`cont-gd-scope-${s}`} /> {s}</label>))}
        </div>
        <div className="text-[9.5px] text-slate-400">
          Personal continuity is never inferred — grant it only to explicitly authorised people.</div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} data-testid="cont-gd-save"
              className="text-[11px] px-3 py-1.5 rounded-md bg-emerald-950 text-white">Grant</button>
        </div>
      </div>
    </Modal>
  );
}

// --------------------------------------------------------- SUCCESSION EDITOR
const SUCC_LABELS = {
  business_continuity_contact: "Business Continuity Contact",
  operational_decision_authority: "Operational Decision Authority",
  finance_contact: "Finance Contact",
  customer_escalation_contact: "Customer Escalation Contact",
  vendor_escalation_contact: "Vendor Escalation Contact",
  technology_administrator: "Technology Administrator",
  legal_adviser: "Legal Adviser",
  ca_auditor: "CA / Auditor",
};

export function SuccessionEditor({ onSaved }) {
  const [roles, setRoles] = useState(null);
  useEffect(() => {
    api.get("/continuity/succession")
      .then(({ data }) => setRoles(data.succession.roles || {}));
  }, []);
  if (!roles) return null;
  const set = (key, field) => (e) =>
    setRoles({ ...roles, [key]: { ...(roles[key] || {}), [field]: e.target.value } });
  const saveAll = async () => {
    try {
      await api.put("/continuity/succession", { roles });
      toast.success("Succession map saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-4" data-testid="cont-succession">
      <div className="flex items-center mb-3">
        <div className="text-[11px] font-heading font-semibold flex-1">Business succession map
          <span className="text-slate-400 font-normal ml-1">(operational guidance — does not replace legal instruments)</span></div>
        <button onClick={saveAll} data-testid="cont-succ-save"
            className="text-[10px] px-2.5 py-1 rounded bg-emerald-950 text-white">Save succession map</button>
      </div>
      <div className="grid md:grid-cols-2 gap-2.5">
        {Object.entries(SUCC_LABELS).map(([k, label]) => (
          <div key={k} className="border border-slate-100 rounded-lg p-2.5">
            <div className="text-[9.5px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>
            <input value={roles[k]?.name || ""} onChange={set(k, "name")} placeholder="Name"
                data-testid={`cont-succ-${k}-name`}
                className="w-full text-[11.5px] border border-slate-200 rounded px-2 py-1 mb-1" />
            <input value={roles[k]?.contact || ""} onChange={set(k, "contact")} placeholder="Phone / email"
                className="w-full text-[11px] border border-slate-200 rounded px-2 py-1" />
          </div>))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- INFO INDEX
export function InfoIndexDialog({ item, onClose, onDone }) {
  const [f, setF] = useState({ scope: item.scope || "BUSINESS", label: item.label || "",
    module_label: item.module_label || "", path: item.path || "",
    what_is_this: item.what_is_this || "", why_it_matters: item.why_it_matters || "",
    where_used: item.where_used || "", who_handles: item.who_handles || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={item.id ? "Edit pointer" : "Add information pointer"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="cont-idx-dialog">
        <Field label="Record label" required>
          <input value={f.label} onChange={set("label")} data-testid="cont-ixd-label" className={inputCls} /></Field>
        <Field label="Scope">
          <select value={f.scope} onChange={set("scope")} className={inputCls}>
            {SCOPES.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Stored in (module name)" required>
          <input value={f.module_label} onChange={set("module_label")} data-testid="cont-ixd-module" className={inputCls} /></Field>
        <Field label="Path" required>
          <input value={f.path} onChange={set("path")} placeholder="/admin/finance-vault"
              data-testid="cont-ixd-path" className={inputCls} /></Field>
        <Field label="What is this?">
          <input value={f.what_is_this} onChange={set("what_is_this")} className={inputCls} /></Field>
        <Field label="Why it matters">
          <input value={f.why_it_matters} onChange={set("why_it_matters")} className={inputCls} /></Field>
        <Field label="Where it is used">
          <input value={f.where_used} onChange={set("where_used")} className={inputCls} /></Field>
        <Field label="Who handles it">
          <input value={f.who_handles} onChange={set("who_handles")} className={inputCls} /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={() => f.label && f.path ? save("/continuity/info-index", item, f, onDone)
            : toast.error("Label and path required")}
            data-testid="cont-ixd-save" className="text-[11px] px-3 py-1.5 rounded-md bg-emerald-950 text-white">Save</button>
      </div>
    </Modal>
  );
}
